import { EmployeeStatus, LegalEntityCountry } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';
import { useData } from '../../../contexts/data-context/use-data';
import { EmployeeView } from '../../../types/entity-types';
import { postSwrFetcher } from '../../../utils/post-swr-fetcher';
import { putSwrFetcherWithDynamicUrl } from '../../../utils/put-swr-fetcher-with-dynamic-url';

type EmployeeRequestType = {
  firstName: string;
  lastName: string;
  companyId: string;
  country: LegalEntityCountry;
  destination: {
    type: string;
    accountType?: string;
    currency: string;
    iban?: string;
    accountNumber?: string;
    sortCode?: string;
  };
};

export type EmployeeExtendedRequestType = EmployeeRequestType & {
  taxId?: string | null;
  role?: string;
  salary?: number;
  address?: {
    line1: string;
    line2?: string;
    postTown: string;
    postCode: string;
    countrySubDivision?: string;
  };
  status?: EmployeeStatus;
};

const EMPLOYEES_API_PATH = '/api/employee';

export const useEmployee = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { data, mutations } = useData();
  const legalEntityCountry = data?.companies[0].legalEntity.country;

  const { trigger: createEmployee } = useSWRMutation<EmployeeView, Error, string, EmployeeExtendedRequestType>(
    EMPLOYEES_API_PATH,
    postSwrFetcher,
  );

  const { trigger: updateEmployee } = useSWRMutation<
    void,
    AxiosError,
    string,
    { urlSuffix: string } & EmployeeExtendedRequestType
  >(EMPLOYEES_API_PATH, putSwrFetcherWithDynamicUrl);

  const handleCreateEmployee = async (formData: EmployeeExtendedRequestType) => {
    if (!legalEntityCountry) {
      toast.error('Legal entity country not found');
      return;
    }

    try {
      setIsLoading(true);

      const employeeData: EmployeeExtendedRequestType = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyId: formData.companyId,
        country: formData.country,
        destination: formData.destination,
        address: formData.address,
        taxId: formData.taxId,
        role: formData.role,
        salary: formData.salary,
        status: formData.status,
      };

      const newEmployee = await createEmployee(employeeData);
      if (newEmployee && data?.employees) {
        await mutations.mutateCache?.({
          employees: [...data.employees, newEmployee],
        });
        toast.success(`Employee ${newEmployee.firstName} ${newEmployee.lastName} created successfully`);
        return newEmployee;
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          formData,
          context: 'handleCreateEmployee',
        },
      });
      toast.error(`Failed to create employee ${formData.firstName} ${formData.lastName}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateEmployeesFromBeneficiaries = async (beneficiaryIds: string[]) => {
    if (!legalEntityCountry) {
      return;
    }

    try {
      setIsLoading(true);

      const createdEmployees: EmployeeView[] = [];
      const errors: string[] = [];

      for (const beneficiaryId of beneficiaryIds) {
        const beneficiary = data?.beneficiaries.find((b) => b.id === beneficiaryId);
        if (beneficiary) {
          // Beneficiary only has a name, we're forcing a split by first and last name.
          const { name } = beneficiary;
          const nameParts = name.trim().split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';

          const employee: EmployeeExtendedRequestType = {
            firstName,
            lastName,
            companyId: beneficiary.company.id,
            country: legalEntityCountry,
            destination: {
              type: beneficiary.destination.type,
              accountType: beneficiary.destination.accountType,
              currency: beneficiary.destination.currency,
              iban: beneficiary.destination.iban,
              accountNumber: beneficiary.destination.accountNumber,
              sortCode: beneficiary.destination.sortCode,
            },
          };

          try {
            const newEmployee = await createEmployee(employee);
            if (newEmployee) {
              createdEmployees.push(newEmployee);
            }
          } catch (error) {
            // TODO: https://linear.app/seapoint/issue/TENG-2038/error-handling-for-failed-employee-creation
            Sentry.captureException(error, {
              extra: {
                beneficiaryId,
                employee,
                context: 'handleCreateEmployeesFromBeneficiaries',
              },
            });
            errors.push(`Failed to create employee for ${beneficiary.name}`);
          }
        }
      }

      // Update cache with all created employees at once
      if (createdEmployees.length > 0 && data?.employees) {
        await mutations.mutateCache?.({
          employees: [...data.employees, ...createdEmployees],
        });
      }

      if (createdEmployees.length > 0) {
        toast.success(`Successfully created ${createdEmployees.length} employee(s)`);
      }

      if (errors.length > 0) {
        toast.error(`Failed to create ${errors.length} employee(s), please try again`);
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          beneficiaryIds,
          context: 'handleCreateEmployeesFromBeneficiaries',
        },
      });
      toast.error('Failed to create employees from beneficiaries');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateEmployee = async (employeeId: string, formData: EmployeeExtendedRequestType) => {
    if (!legalEntityCountry) {
      toast.error('Legal entity country not found');
      return;
    }

    try {
      setIsLoading(true);

      const employeeData: EmployeeExtendedRequestType = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyId: formData.companyId,
        country: formData.country,
        destination: formData.destination,
        address: formData.address,
        taxId: formData.taxId,
        role: formData.role,
        salary: formData.salary,
        status: formData.status,
      };

      await updateEmployee({ urlSuffix: `/${employeeId}`, ...employeeData });
      await mutations.mutateCache?.({
        employees: data?.employees.map((emp) =>
          emp.id === employeeId ? ({ ...emp, ...employeeData } as unknown as EmployeeView) : emp,
        ),
      });
      toast.success(`Employee ${formData.firstName} ${formData.lastName} updated successfully`);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          formData,
          context: 'handleCreateEmployee',
        },
      });
      toast.error(`Failed to update employee ${formData.firstName} ${formData.lastName}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    createEmployee,
    handleCreateEmployee,
    handleCreateEmployeesFromBeneficiaries,
    handleUpdateEmployee,
  };
};
